import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button} from 'react-bootstrap';
import {FaCheck, FaFolderPlus, FaMagic, FaPlayCircle} from 'react-icons/fa';
import Configuration from '../config';
import replayerlogoBianco from '../images/replayer_logo_bianco_40x32px.png';

const STATUS_CONTENT = {
  live_delayed: {badge: 'PUBLIC_CATALOG_LIVE_DELAYED', help: 'PUBLIC_CATALOG_LIVE_DELAYED_HELP'},
  scheduled: {
    badge: 'HOME_PURCHASED_VIDEO_SCHEDULED',
    help: 'HOME_PURCHASED_VIDEO_SCHEDULED_HELP'
  },
  live_waiting_upload: {
    badge: 'HOME_PURCHASED_VIDEO_LIVE_WAITING',
    help: 'HOME_PURCHASED_VIDEO_LIVE_WAITING_HELP'
  },
  live_uploading: {
    badge: 'HOME_PURCHASED_VIDEO_LIVE',
    help: 'HOME_PURCHASED_VIDEO_LIVE_HELP',
    live: true
  },
  waiting_upload: {
    badge: 'HOME_PURCHASED_VIDEO_WAITING_UPLOAD',
    help: 'HOME_PURCHASED_VIDEO_WAITING_UPLOAD_HELP'
  },
  uploading: {
    badge: 'HOME_PURCHASED_VIDEO_UPLOADING',
    help: 'HOME_PURCHASED_VIDEO_UPLOADING_HELP'
  },
  processing: {
    badge: 'HOME_PURCHASED_VIDEO_PROCESSING',
    help: 'HOME_PURCHASED_VIDEO_PROCESSING_HELP'
  },
  ready: {
    badge: 'HOME_PURCHASED_VIDEO_READY',
    help: 'HOME_PURCHASED_VIDEO_READY_HELP'
  }
};

export const purchasedVideoStatusContent = status => (
  STATUS_CONTENT[status] || STATUS_CONTENT.waiting_upload
);

const formatDate = (value, locale) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'});
};

const formatTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const PurchasedVideoHomeCard = ({video, locale, t, onPlay, onCreateHighlight, onCopy}) => {
  const [copying, setCopying] = useState(false);
  const [copiedLocally, setCopiedLocally] = useState(false);
  const status = purchasedVideoStatusContent(video.playbackStatus);
  const canPlay = Boolean(video.playable && video.token);
  const canCreateHighlight = Boolean(video.highlightAvailable && video.token);
  const copied = Boolean(video.copiedToMyVideo || copiedLocally);
  const canCopy = Boolean(video.copyAvailable && !copied);
  const expiryDate = formatDate(video.videoExpiry, locale);
  const fieldImage = Array.isArray(video.fieldImage) ? video.fieldImage[0] : video.fieldImage;
  const resourceImageUrl = video.fieldId
    ? `${Configuration.apiEndPoint}/partners/fields/${video.fieldId}/images?load=${fieldImage}`
    : `${Configuration.apiEndPoint}/partners/services/${video.serviceId}/images?load=${fieldImage}`;
  const backgroundStyle = video.partnerId && fieldImage ? {backgroundImage: `url(${resourceImageUrl})`} : null;
  const copyVideo = async () => {
    if (!canCopy || copying) return;
    setCopying(true);
    try {
      const response = await onCopy(video);
      if (response && (response.status === 201 || (response.json && response.json.error === 'video_already_there'))) {
        setCopiedLocally(true);
      }
    } finally {
      setCopying(false);
    }
  };

  return (
    <article
      className="home-redesign-rail-card home-redesign-rail-card--streaming"
      data-playback-status={video.playbackStatus}
    >
      <button
        type="button"
        className="home-redesign-media"
        disabled={!canPlay}
        onClick={canPlay ? () => onPlay(video) : undefined}
        aria-label={canPlay ? t('HOME_PURCHASED_VIDEO_WATCH') : t(status.badge)}
      >
        <div className="home-redesign-media__image home-redesign-media__image--neutral" style={backgroundStyle}>
          <span className={`home-redesign-badge ${status.live ? 'home-redesign-badge--live' : 'home-redesign-badge--warm'}`}>
            {t(status.badge)}
          </span>
          <span className="home-redesign-media__logo"><img src={replayerlogoBianco} alt="" aria-hidden="true"/></span>
          <span className="home-redesign-media__overlay-text">{video.description ? video.description.substring(0, 56) : ''}</span>
        </div>
      </button>
      <div className="home-redesign-rail-card__body">
        <div className="home-redesign-rail-card__meta">
          <strong>{video.description || t('PLAYER_HOME_NEW_VIDEOS')}</strong>
          <span>{video.partnerName}</span>
        </div>
        <div className="home-redesign-rail-card__details">
          <span>{video.fieldName}</span>
          <span>{formatDate(video.startDatetime, locale)} · {formatTime(video.startDatetime)}</span>
        </div>
        <p className="home-redesign-purchased-video-status">{t(status.help)}</p>
        <p className={`home-redesign-purchased-video-expiry ${copied ? 'home-redesign-purchased-video-expiry--saved' : ''}`}>
          {copied ? (
            <><FaCheck aria-hidden="true"/>{t('HOME_PURCHASED_VIDEO_SAVED')}</>
          ) : expiryDate ? (
            t('HOME_PURCHASED_VIDEO_EXPIRES', {date: expiryDate})
          ) : null}
        </p>
        {canPlay || canCreateHighlight ? (
          <div className={`home-redesign-rail-card__actions ${canPlay && canCreateHighlight ? 'home-redesign-rail-card__actions--split' : ''}`}>
            {canPlay ? (
              <Button type="button" className="btn home-skywork-action home-skywork-action--primary" onClick={() => onPlay(video)}>
                <FaPlayCircle aria-hidden="true"/>
                {t('HOME_PURCHASED_VIDEO_WATCH')}
              </Button>
            ) : null}
            {canCreateHighlight ? (
              <Button type="button" className="btn home-skywork-action home-skywork-action--secondary" onClick={() => onCreateHighlight(video)}>
                <FaMagic aria-hidden="true"/>
                {t('CREATE_HIGHLIGHT')}
              </Button>
            ) : null}
          </div>
        ) : null}
        <Button
          type="button"
          className="btn home-skywork-action home-skywork-action--copy"
          disabled={!canCopy || copying}
          onClick={copyVideo}
          title={!video.videoComplete && !copied ? t('HOME_PURCHASED_VIDEO_COPY_WAIT') : undefined}
        >
          {copied ? <FaCheck aria-hidden="true"/> : <FaFolderPlus aria-hidden="true"/>}
          {copying ? t('HOME_PURCHASED_VIDEO_COPYING') : copied ? t('HOME_PURCHASED_VIDEO_COPIED') : t('COPY_TO_MY_VIDEO')}
        </Button>
        {!video.videoComplete && !copied ? (
          <small className="home-redesign-purchased-video-copy-help">{t('HOME_PURCHASED_VIDEO_COPY_WAIT')}</small>
        ) : null}
      </div>
    </article>
  );
};

PurchasedVideoHomeCard.propTypes = {
  video: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onCreateHighlight: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired
};

export default PurchasedVideoHomeCard;
